import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import React, { useState, useContext, useRef, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormControl, FormGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { FormContext, skipUpdate } from '../FormContext';
import FormInputLabel from './parts/FormInputLabel';
import FormInputHelp from './parts/FormInputHelp';
import FormInputToolbar from './parts/FormInputToolbar';
import FormInputIcon from './parts/FormInputIcon';
import CalcExplain from './parts/CalcExplain';

const FormInputItem = React.memo(
  React.forwardRef((props, ref) => {
    const { field, calculated, controlId, disabled, noLabel, touched, originalValue } = props;

    const consumerUpdate = useRef(false);
    const firstUpdate = useRef(true);
    const controlIcon = useRef(null);

    const [value, setValue] = useState(props.value);
    const [valid, setValid] = useState(props.valid);
    const [errorMessage, setErrorMessage] = useState(props.errorMessage);
    const [dirty, setDirty] = useState(!isEqual(props.value, props.originalValue));
    const [warning, setWarning] = useState(props.warning);
    const [info, setInfo] = useState(props.info);

    const { ee, trackDirty } = useContext(FormContext);

    const isNumberField =
      field.type === 'number' || field.type === 'integer' || field.type === 'currency' || field.type === 'percent';
    const isEnabled = !field.readOnly && !disabled;

    const clear = () => {
      props.onChange(field.name, '');
      setDirty(false);
      setErrorMessage('');
      setValid(true);
      setValue('');
      setWarning('');
    };

    const whichIcon = () => {
      if (field.icon) {
        return field.icon;
      }

      if (field.type === 'creditcard') {
        return 'card';
      }

      if (field.type === 'phone') {
        return 'phone';
      }

      if (field.readOnly && !field.displayOnly) {
        // return 'lock';
        return null;
      }

      return null;
    };

    const onBlur = () => {
      const result = props.onBlur(field.name);

      if (result) {
        // This is so the blur side of the focus logic doesn't trigger a useEffect on [value] block
        if (isNumberField && isEnabled) {
          if (result.displayValue && result.displayValue.replace) {
            if (result.displayValue.replace(/[$,]/g, '') === result.rawValue) {
              firstUpdate.current = true; // Skip useEffect, validator displayValue doesn't need to update rawValue
            }
          }
        }

        setValue(result.displayValue !== undefined ? result.displayValue : value);
        setValid(result.valid !== undefined ? result.valid : valid);
        setErrorMessage(result.validError !== undefined ? result.validError : errorMessage);
        setDirty(!isEqual(originalValue, value));
        return true;
      }

      return false;
    };

    const handleFocus = event => {
      if (isNumberField && isEnabled) {
        if (value && value.replace) {
          // Only do a change/skip useEffect if a change is actually occurring
          if (value !== value.replace(/[$,]/g, '')) {
            firstUpdate.current = true; // Skip useEffect callback onvalue to onchange isn't triggered
            setValue(val => val.replace(/[$,]/g, ''));
          }
        }
        const target = event.target;
        // Using setTimeout as a ghetto callback for setValue's new value
        setTimeout(() => {
          // Prevents focus/blur loops
          if (target === document.activeElement) {
            target.select();
          }
        }, 1);
      }
    };

    const consume = ({ action, payload: data }) => {
      if (action === 'clear') {
        clear();
        return;
      }

      if (action !== 'update' || !data) {
        return;
      }

      if (data.value !== undefined) {
        consumerUpdate.current = true;
        setValue(data.value);
      }

      if (data.warning !== undefined) {
        setWarning(data.warning);
      }

      if (data.info !== undefined) {
        setInfo(data.info);
      }
    };

    const renderCalcKey = () => {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="auto"
          overlay={
            <Popover className="calc-popover">
              <Popover.Title as="h3">How was this calculated?</Popover.Title>
              <Popover.Content>
                <CalcExplain fieldName={field.name} />
              </Popover.Content>
            </Popover>
          }
        >
          <span style={{ paddingLeft: '3px', color: '#bbb', borderBottom: '1px dotted #bbb' }}>
            <i className="zmdi zmdi-key" />
          </span>
        </OverlayTrigger>
      );
    };

    // Original Value changed from props (usually a submit)
    useEffect(() => {
      if (isEqual(originalValue, value)) {
        setDirty(false);
      } else {
        setDirty(true);
      }
    }, [originalValue, value]);

    // Validation state changed from props (usually a submit)
    useEffect(() => {
      if (props.valid !== valid) {
        setValid(props.valid);
        setErrorMessage(props.errorMessage);
      }
    }, [props.valid]);

    useEffect(() => {
      const isAutoCalculated = calculated !== false;
      if (isAutoCalculated || props.alwaysUpdate) {
        setValue(props.value);
      }
    }, [props.value]);

    useEffect(() => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }

      // TODO Remove stupid array crap at this level
      const dirty = !isEqual(originalValue, value);

      setDirty(dirty);
      props.onChange(field.name, value);

      if (consumerUpdate.current) {
        onBlur();
      }
    }, [value]);

    useEffect(() => {
      props.setWarning(field.name, warning);
    }, [warning]);

    useEffect(() => {
      props.setInfo(field.name, info);
    }, [info]);

    useEffect(() => {
      if (ee) {
        ee.on(controlId, (...args) => {
          setTimeout(() => {
            consume(...args);
          }, 10);
        });
      }

      return () => {
        if (ee) {
          ee.off(controlId);
        }
      };
    }, []);

    useEffect(() => {
      // Make sure this flag is cleared every render
      consumerUpdate.current = false;
    });

    const hasToolbar = field.toolbar && field.toolbar.length > 0;
    const isAutoCalculated = calculated !== false;
    const currentValue = value === null || value === undefined ? '' : value;

    const additionalProps = {};
    const additionalControlProps = {};

    // Invalid as far as when the UI should show it
    const isInvalid = !valid && touched && !field.readOnly;

    // Dirty as far as when the UI should show it
    const isDirty = dirty && valid && !field.readOnly && !field.displayOnly && trackDirty;

    const isReadOnly = field.readOnly || disabled || isAutoCalculated;

    // Default field type is text, also not using html5 date, number, and color fields
    const type =
      field.type && field.type !== 'date' && field.type !== 'number' && field.type !== 'color' ? field.type : 'text';

    const icon = whichIcon();

    const containerClass = classNames({
      'has-icon': !!icon,
      'is-enabled': !isReadOnly,
      'is-invalid': isInvalid,
      'is-dirty': isDirty,
      'is-number': isNumberField,
      'is-readonly': isReadOnly,
      'item-id': field.name === 'itemid',
      ...props.containerClass,
    });

    /* Only text because other types might have display values that break
     max length constraints */
    if (type === 'text' || type === 'string') {
      additionalControlProps.max = field.max;
      additionalControlProps.maxLength = field.maxLen;
    }

    // if (field.calculate) {
    //   value = `=${value}`;
    // }

    // Don't allow tab focus on calculated and read only fields
    if (calculated || field.readOnly) {
      additionalControlProps.tabIndex = '-1';
    }

    // Ignore last pass auto-fill
    // TODO should be a way to enable for login page
    additionalControlProps['data-lpignore'] = true;

    if (type === 'password') {
      // disables autocomplete in Chrome, doesn't work in FireFox
      additionalControlProps.autoComplete = 'new-password';
    }

    if (field.displayOnly) {
      return (
        <div className={`form-group display-only ${containerClass}`}>
          <FormInputLabel hide={noLabel}>
            {field.label}
            {isAutoCalculated && renderCalcKey()}
          </FormInputLabel>
          <div className="displayonly-container">
            <div className="value">
              {field.type === 'currency' && currentValue !== '' ? (
                <>
                  <span className="dollar">$</span>
                  {currentValue.split('$')}
                </>
              ) : (
                currentValue
              )}
            </div>
            {hasToolbar === true && (
              <FormInputToolbar
                toolbar={get(field, 'toolbar')}
                disabled={!currentValue}
                value={currentValue}
                setValue={setValue}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <FormGroup
        controlId={controlId}
        className={containerClass}
        key={controlId}
        id={`${controlId}-control`}
        {...additionalProps}
      >
        <FormInputLabel hide={noLabel}>
          {field.label} {isAutoCalculated && renderCalcKey()}
        </FormInputLabel>
        <div className="input-container">
          <FormControl
            type={type}
            required={field.required}
            minLength={field.minLen}
            pattern={field.pattern}
            min={field.min}
            readOnly={isReadOnly}
            value={currentValue}
            onChange={e => setValue(e.target.value)}
            onBlur={onBlur}
            onFocus={handleFocus}
            disabled={disabled}
            ref={ref}
            placeholder={field.placeholder}
            {...additionalControlProps}
          />
          {hasToolbar === true && (
            <FormInputToolbar
              toolbar={get(field, 'toolbar')}
              disabled={!currentValue}
              value={currentValue}
              setValue={setValue}
            />
          )}
        </div>
        {props.children}
        <FormInputIcon icon={whichIcon()} ref={controlIcon} />
        {warning && <div className="warning-block">{warning}</div>}
        {info && <div className="info-block">{info}</div>}
        <FormInputHelp errorText={errorMessage}>{field.helpText}</FormInputHelp>
      </FormGroup>
    );
  }),
  skipUpdate
);

FormInputItem.defaultProps = {
  alwaysUpdate: false,
  calculated: false,
  // key / bool pairs passed to classnames for container
  children: null,
  containerClass: {},
  disabled: false,
  errorMessage: '',
  field: {},
  info: '',
  noLabel: false,
  onBlur: () => {},
  onChange: () => {},
  originalValue: '',
  setInfo: () => {},
  setWarning: () => {},
  touched: false,
  valid: true,
  value: '',
  warning: '',
};

FormInputItem.propTypes = {
  alwaysUpdate: PropTypes.bool,
  calculated: PropTypes.bool,
  children: PropTypes.any,
  containerClass: PropTypes.object,
  controlId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  info: PropTypes.string,
  noLabel: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  originalValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    // Sometimes hidden fields just need to store arbitrary data
    PropTypes.array,
  ]),
  setInfo: PropTypes.func,
  setWarning: PropTypes.func,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    // Sometimes hidden fields just need to store arbitrary data
    PropTypes.array,
  ]),
  warning: PropTypes.string,
};

export default FormInputItem;
